import React, { FC, useEffect, useState } from "react";
import { useAppSelector } from "../../store/hooks";
import IRevision from "../../types/revision";
import { isIP, limit } from "../../utils/convert";
import ProfileIcon from "./icons/ProfileIcon";
import { wikiAPI } from "../../api/wp";
import { decodeHtmlEntity } from "../../utils/parsing";
import classNames from "classnames";
import LoadingIcon from "./icons/LoadingIcon";
import RevisionsSorting from "./RevisionsSorting";
import PlusIcon from "./icons/PlusIcon";
import endpoints from "../../endpoints";
import Modal from "../modals/Modal";

type TAnalyzedText = {
  notices: string[];
  importance: string;
};

const revisionNotices: Record<string, string> = {
  'mw-rollback': 'Revision was rollback',
  'mw-reverted': 'Revision was reverted',
}

const Revisions = () => {
  const authUser = useAppSelector(state => state.auth.authUser)
  const searchPage = useAppSelector((state) => state.search.page);
  const commonOptions = useAppSelector((state) => state.common.options);
  const [loadingRevisionId, setLoadingRevisionId] = useState<number>(0);
  const [analyzedText, setAnalyzedText] = useState<TAnalyzedText | null>(null);
  const [analyzedAIText, setAnalyzedAIText] = useState<string>("");
  const [openAnalyzeModal, setOpenAnalyzeModal] = useState<boolean>(false);
  const [openAIAnalyzeModal, setOpenAIAnalyzeModal] = useState<boolean>(false);
  const [openFullStatisticModal, setOpenFullStatisticModal] =
    useState<boolean>(false);
  const revisions: IRevision[] = useAppSelector(
    (state) => state.search.revisions
  );
  const activeRevision = useAppSelector((state) => state.search.activeRevision);
  const [
    getRevisionContent,
    { isLoading: isRevisionLoading, isSuccess: isRevisionSuccess },
  ] = wikiAPI.useGetRevisionContentMutation();
  const [
    getRevisions,
    { isLoading: getRevisionsLoading, isSuccess: getRevisionsSuccess },
  ] = wikiAPI.useGetRevisionsMutation();
  const [getRevisionAIAnalysis, { isLoading: getRevisionAIAnalysisLoading }] =
    wikiAPI.useGetRevisionAIAnalysisMutation();
  const [getRevisionAnalysis, { isLoading: getRevisionAnalysisLoading }] =
    wikiAPI.useGetRevisionAnalysisMutation();
  const [fullStatisticLoading, setFullStatisticLoading] =
    useState<boolean>(false);

  useEffect(() => {
    if (!isRevisionLoading) {
      setLoadingRevisionId(0);
    }
  }, [isRevisionLoading]);

  useEffect(() => {
    if (analyzedAIText) {
      setOpenAIAnalyzeModal(true);
    } else {
      setOpenAIAnalyzeModal(false);
    }
  }, [analyzedAIText]);

  useEffect(() => {
    if (analyzedText) {
      setOpenAnalyzeModal(true);
    } else {
      setOpenAnalyzeModal(false);
    }
  }, [analyzedText]);

  if (!revisions.length) {
    return <></>;
  }

  const chooseRevision = (revision: IRevision) => {
    setLoadingRevisionId(revision.id);
    setOpenAIAnalyzeModal(false);
    setOpenAnalyzeModal(false);
    setAnalyzedAIText("");
    setAnalyzedText(null);
    setTimeout(() => getRevisionContent(revision), 100);
  };

  const loadMoreRevisions = () => {
    if (!searchPage || !searchPage.id) return;

    const lastParentRevisionId = revisions.length
      ? revisions[revisions.length - 1].parentId
      : "";
    if (!lastParentRevisionId) return;

    getRevisions({
      pageId: searchPage.id,
      limit: commonOptions.revisionsStep,
      fromRevisionId: lastParentRevisionId,
    });
  };

  const getAuthorUrl = (username: string) => {
    return endpoints.contributions.replace("{username}", username);
  };

  const getAuthorLabel = (username: string) => {
    return isIP(username) ? "Anonym" : "Author";
  };

  const launchRevisionAIAnalyze = async () => {
    if (analyzedAIText) {
      setOpenAIAnalyzeModal(true);
      return;
    }
    if (activeRevision?.content?.length) {
      const data = await getRevisionAIAnalysis(activeRevision.content);
      if (data?.data?.result) {
        setAnalyzedAIText(data.data.result);
      }
    }
  };

  const launchRevisionAnalyze = async () => {
    if (analyzedText) {
      setOpenAnalyzeModal(true);
      return;
    }
    if (activeRevision?.content) {
      const data = await getRevisionAnalysis(activeRevision);
      if (data?.data?.importance && data.data.notices) {
        setAnalyzedText({
          notices: data.data.notices,
          importance: data.data.importance,
        });
      }
    }
  };

  const launchGetFullStatistic = async () => {
    setOpenFullStatisticModal(true);
    setFullStatisticLoading(true);
  };

  return (
    <section className="py-[40px]">
      <div className="container">
        <div className="text-gray-700">
          <div className="flex items-start mb-[40px] justify-center md:justify-between flex-col md:flex-row">
            <h2 className="mb-[50px] text-xl font-bold text-gray-900 md:text-2xl lg:text-3xl inline-flex mb-3 md:mb-0 w-full md:w-auto justify-center md:justify-start">
              <mark className="px-2 py-1 text-white bg-[#546E7A] rounded">
                Revisions
              </mark>
            </h2>
          </div>
          <div className="flex flex-wrap md:flex-nowrap">
            <div className="w-full md:w-[440px] md:min-w-[440px] max-h-[500px] overflow-y-auto md:mr-4 mb-6 md:mb-0">
              <ul className="flex-column text-sm font-medium text-gray-500 md:me-1 mb-4 md:mb-0">
                {revisions.map((revision, i) => (
                  <li className="mb-1" key={revision.id}>
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        chooseRevision(revision);
                      }}
                      href="#"
                      className={classNames(
                        "relative block py-1 px-3 md:py-3 text-[#111111] rounded-lg w-full",
                        {
                          "bg-gray-100 hover:bg-gray-200":
                            revision.id !== activeRevision?.id &&
                            revision.id !== loadingRevisionId,
                          "bg-[#546E7A] text-white":
                            revision.id === activeRevision?.id ||
                            revision.id === loadingRevisionId,
                        }
                      )}
                    >
                      <div
                        className={classNames("flex items-center", {
                          "opacity-10": revision.id === loadingRevisionId,
                        })}
                      >
                        {`${revision.date}`}
                        <div
                          className={classNames(
                            "flex ml-[20px] py-1 px-2 bg-gray-200 rounded-lg text-gray-700 items-center",
                            {}
                          )}
                        >
                          <ProfileIcon width={15} height={15} />
                          <span className="ml-1" title={revision.username}>
                            {limit(revision.username, 9)}
                          </span>
                        </div>
                      </div>
                      {revision.id === loadingRevisionId && (
                        <div className="absolute left-15 top-0 items-center flex h-full">
                          <LoadingIcon />
                        </div>
                      )}
                    </a>
                  </li>
                ))}

                <li
                  className={classNames(
                    "mb-1 relative block text-[#111111] rounded-lg w-full text-white bg-gray-200 hover:bg-gray-300",
                    {
                      "bg-[#546E7A] hover:bg-[#546E7A]": getRevisionsLoading,
                    }
                  )}
                >
                  <div
                    className="flex justify-center items-center cursor-pointer h-[40px]"
                    onClick={loadMoreRevisions}
                  >
                    {getRevisionsLoading ? (
                      <LoadingIcon width={24} height={24} />
                    ) : (
                      <PlusIcon width={24} height={24} />
                    )}
                  </div>
                </li>
              </ul>
            </div>
            <div
              className={classNames("w-full ml-3 revision overflow-auto", {
                invisible: loadingRevisionId,
              })}
            >
              {!!activeRevision && (
                <div className="mb-4 flex flex-col md:block flex-wrap">
                  <span className="mr-4 bg-gray-200 px-3 py-2 rounded-lg inline-block">
                    <b>Date: </b>
                    {activeRevision.date}
                  </span>
                  <span className="mr-4 bg-gray-200 px-3 py-2 rounded-lg inline-block">
                    <b>{getAuthorLabel(activeRevision.username)}: </b>
                    <a
                      target="_blank"
                      href={getAuthorUrl(activeRevision.username)}
                    >
                      {activeRevision.username}
                    </a>
                  </span>
                  <span className="mr-4 bg-gray-200 px-3 py-2 rounded-lg inline-block">
                    <b>Size: </b>
                    {activeRevision.size} bytes
                  </span>
                  <div className="mb-2 mt-2">
                    <span
                      className={classNames(
                        "mr-4 bg-gray-800 hover:bg-gray-600 text-white px-3 py-2 rounded-lg inline-block cursor-pointer",
                        { "bg-gray-600": getRevisionAIAnalysisLoading }
                      )}
                      onClick={launchRevisionAIAnalyze}
                    >
                      <span
                        className={classNames("", {
                          invisible: getRevisionAIAnalysisLoading,
                        })}
                      >
                        AI-based analysis
                      </span>
                      {getRevisionAIAnalysisLoading ? <LoadingIcon /> : <></>}
                    </span>
                    <span
                      className={classNames(
                        "mr-4 bg-gray-800 hover:bg-gray-600 text-white px-3 py-2 rounded-lg inline-block cursor-pointer",
                        { "bg-gray-600": getRevisionAnalysisLoading }
                      )}
                      onClick={launchRevisionAnalyze}
                    >
                      <span
                        className={classNames("", {
                          invisible: getRevisionAnalysisLoading,
                        })}
                      >
                        Analysis
                      </span>
                      {getRevisionAnalysisLoading ? <LoadingIcon /> : <></>}
                    </span>
                    {!!authUser?.isAdmin && <span
                      className={classNames(
                        "mr-4 bg-gray-800 hover:bg-gray-600 text-white px-3 py-2 rounded-lg inline-block cursor-pointer",
                        { "bg-gray-600": fullStatisticLoading }
                      )}
                      onClick={launchGetFullStatistic}
                    >
                      <span
                        className={classNames("", {
                          invisible: fullStatisticLoading,
                        })}
                      >
                        Full statistics
                      </span>
                      {fullStatisticLoading ? <LoadingIcon /> : <></>}
                    </span>}
                  </div>
                </div>
              )}
              <RevisionContent />
            </div>
          </div>
        </div>
      </div>
      {openAnalyzeModal && !!analyzedText && (
        <Modal onClose={() => setOpenAnalyzeModal(false)}>
          <Analysis
            notices={analyzedText.notices}
            importance={analyzedText.importance}
          />
        </Modal>
      )}
      {openAIAnalyzeModal && !!analyzedAIText && (
        <Modal onClose={() => setOpenAIAnalyzeModal(false)}>
          <div>
            {analyzedAIText}
            <br />
            <div className="font-bold mt-4">Powered by ChatGPT</div>
          </div>
        </Modal>
      )}
      {openFullStatisticModal && searchPage && (
        <Modal
          visible={openFullStatisticModal && !fullStatisticLoading}
          onClose={() => setOpenFullStatisticModal(false)}
          fullScreen={true}
        >
          <iframe
            onLoad={() => {
              setFullStatisticLoading(false);
              setOpenFullStatisticModal(true);
            }}
            width={"100%"}
            height={"100%"}
            src={`${endpoints.xToolUrl}/${searchPage.title}`}
            frameBorder="0"
          />
        </Modal>
      )}
    </section>
  );
};

const RevisionContent = () => {
  const activeRevision = useAppSelector((state) => state.search.activeRevision);

  if (!activeRevision) {
    return <></>
  }

  if (!activeRevision?.content || activeRevision.content.length < 2) {
    return <>(No difference)</>
  }

  if (!!activeRevision.content) {
    return <div className="revision-content">
      <div
        dangerouslySetInnerHTML={{
          __html: decodeHtmlEntity(activeRevision.content),
        }}
        className="mb-2"
      />
    </div>
  }

  return <div className="revision-content">
    <RevisionNotices />
  </div>
}

const RevisionNotices = () => {
  const activeRevision = useAppSelector((state) => state.search.activeRevision);
  if (!activeRevision?.tags || !activeRevision.tags.length) {
    return <></>
  }

  const getNotices = () => {
    if (!activeRevision.tags) return [];
    return activeRevision.tags.map(tag => Object.keys(revisionNotices).includes(tag))
  }

  return <>
    {activeRevision.tags.map(tag => Object.keys(revisionNotices).includes(tag) && <div className="mb-">{revisionNotices[tag]}</div>)}
  </>
}

const Analysis: FC<TAnalyzedText> = ({ notices, importance }) => {
  return (
    <div>
      <p>
        <b>Notices:</b>
      </p>
      {notices.length ? notices.map((notice) => <p>{notice}</p>) : "No"}
      <br />
      <p>
        <b>Importance: </b>
        {importance}
      </p>
    </div>
  );
};

export default Revisions;
